import * as React from 'react';
import Link from 'next/link';
import { CardCarousel } from '@components/card-carousel';
import { api } from '@/trpc/react';
import { Button } from '@components/ui/button';
import { CardGrid } from '@components/ui/CardGrid';
import { cn } from '@/lib/utils';
import { ThemeConfiguration } from '@/atoms/useTrainingAtom';
import { useTrackClientEvent } from '@analytics/trackClientEvent';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { PhotoPackV3 } from '../PhotoPack/PhotoPackV3';
import { PhotoPackDetails } from '@components/photo-pack-details';
import usePhotoPackAction from '@/atoms/usePhotoPackAction';
import { DEFAULT_PHOTO_ASPECT_RATIO, getDefaultWorkflowName } from '@acme/lib';
import { useSession } from '@hooks/index';
import { PhotoPackVariant } from '@components/PhotoPack/PhotoPackV3/utils';
import { CloudflareTransform } from '@lib/transformCloudflareImageUrl';
import { useCartAtom } from '@/atoms/useCartAtom';

export interface PhotoPackListProps {
  onPhotoPackSelected?: (theme: Pick<ThemeConfiguration, 'id' | 'name'>) => void;
  selectedPhotoPacks?: Pick<ThemeConfiguration, 'id'>[];
  hrefBase?: string;
  enableSampleGallery?: boolean;
  listTitle?: string;
  listDescription?: string;
  showUsageCount?: boolean;
  alignHeader?: 'left' | 'center';
  tags?: string[];
  limit?: number;
  sortBy?: 'TOP' | 'NEWEST' | 'TRENDING';
  variant?: PhotoPackVariant;
  listType?: 'grid' | 'carousel';
  isGroupPhotoPack?: boolean;
  isPublic?: boolean;
  themeIds?: string[];
  relationship?: 'couple' | 'friends';
  columns?: {
    default?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    '2xl'?: number;
  };
  isCollapsible?: boolean;
  initialVisibleItems?: number;
  rowsToShow?: number;
  timeRange?: 'last_hour' | 'last_24_hours' | 'last_7_days' | 'last_30_days' | 'last_365_days' | 'all';
  displayLimit?: number;
  shuffle?: boolean;
  mode: 'training' | 'cart';
  disabledLink?: boolean;
  hideListTitleAndShowAll?: boolean;
  autoplay?: boolean;
  showSelfies?: boolean;
  cloudflareVariant?: CloudflareTransform['variant'];
  className?: string;
  onThemeAdded?: () => void;
  isOnboarding?: boolean;
  modelLocked?: boolean;
  teamId?: string;
  memberId?: string;
}

export const PhotoPackList = ({
  selectedPhotoPacks,
  enableSampleGallery,
  listTitle,
  listDescription,
  alignHeader = 'left',
  tags,
  limit,
  sortBy,
  variant = 'samples',
  showUsageCount = false,
  listType = 'carousel',
  isGroupPhotoPack = false,
  isPublic = true,
  themeIds,
  relationship,
  columns = {
    default: 2,
    sm: 2,
    md: 3,
    lg: 4,
    xl: 4,
    '2xl': 6
  },
  isCollapsible = false,
  rowsToShow = 2,
  timeRange = 'last_7_days',
  displayLimit = Infinity,
  shuffle = false,
  mode,
  onPhotoPackSelected,
  hrefBase = '/packs/browse',
  disabledLink = false,
  hideListTitleAndShowAll = false,
  autoplay = false,
  showSelfies = false,
  cloudflareVariant = 'public',
  className,
  onThemeAdded,
  isOnboarding = false,
  modelLocked = false,
  teamId,
  memberId
}: PhotoPackListProps) => {
  const { trackEvent } = useTrackClientEvent();
  const [isOpen, setIsOpen] = React.useState(false);
  const { user, isLoggedIn } = useSession();
  const [selectedThemeId, setSelectedThemeId] = React.useState<string>();
  const [isPhotoPackDetailsOpen, setIsPhotoPackDetailsOpen] = React.useState(false);

  const photoPacksQuery = api.theme.getThemeList.useQuery(
    {
      tags,
      limit,
      sortBy,
      mode: isGroupPhotoPack ? 'group' : 'single',
      isPublic,
      themeIds,
      relationship,
      timeRange
    },
    {
      refetchOnWindowFocus: false
    }
  );
  const {
    addTheme,
    themes,
    updateThemePhotoSize,
    updateThemeQuantity,
    updateThemeWorkflow,
    updateThemeGarmentImages,
    removeTheme,
    handleThemeSelection
  } = usePhotoPackAction({
    mode
  });

  const { setModel } = useCartAtom();

  // If memberId is provided, use it to get the credits for the specific member
  // This could happen if the user with session is the owner of the team and they want to generate a pack for a specific member
  const teamCreditsQuery = api.teamMemberCredits.get.useQuery(
    { teamId: teamId!, ...(memberId ? { userId: memberId } : {}) },
    { enabled: !!teamId }
  );
  const userCreditsQuery = api.userCredits.get.useQuery({ userId: user?.id! }, { enabled: !!user?.id });
  const selectedTheme = themes?.find(theme => theme.id === selectedThemeId);

  // Add shuffled data calculation
  const displayData = React.useMemo(() => {
    if (!photoPacksQuery.data) return [];

    if (!shuffle) return photoPacksQuery.data;

    // Create a copy and shuffle it
    return [...photoPacksQuery.data].sort(() => Math.random() - 0.5);
  }, [photoPacksQuery.data, shuffle]);

  // Use the first category or tag for the URL
  const showAllHref = tags?.[0] ? `/headshot-types/${tags?.[0]}` : null;
  const totalPacksInCart = themes?.reduce((sum, theme) => sum + (theme.quantity || 1), 0) || 0;
  // Get available credits
  const availableCredits = teamId
    ? teamCreditsQuery.data?.photoPackCredits || 0
    : mode === 'training'
    ? userCreditsQuery.data?.photoPackCredits || 0
    : Infinity;

  const handleThemeSelected = async (theme: Pick<ThemeConfiguration, 'id'>) => {
    // Use the more comprehensive theme selection logic from the hook
    await handleThemeSelection(theme, {
      // Credit check callback - only used in cart mode
      creditCheck: themeId => {
        if (mode !== 'cart') return true;

        const existingTheme = themes?.find(t => t.id === themeId);
        const isUpdating = !!existingTheme;

        if (!isUpdating && totalPacksInCart >= availableCredits) {
          alert(
            teamId
              ? "You've reached your team's photo pack credit limit. Please contact your workspace owner for more credits."
              : "You've reached the maximum number of photo packs allowed."
          );
          return false;
        }
        return true;
      },
      // For training mode, pass through required validation params
      ...(mode === 'training'
        ? {
            themesAllowed: availableCredits,
            isLoggedIn: !!user,
            teamId
          }
        : {}),
      // Called when theme can be selected
      onThemeSelected: themeId => {
        setSelectedThemeId(themeId);
        setIsPhotoPackDetailsOpen(true);
      }
    });
  };

  const handleSubmit = (data: ThemeConfiguration) => {
    const existingTheme = themes?.find(theme => theme.id === data.id);
    const newQuantity = data.quantity || 1;
    const oldQuantity = existingTheme?.quantity || 0;
    const quantityDiff = newQuantity - oldQuantity;

    // Check if the new quantity would exceed available credits
    if (isLoggedIn && !isOnboarding && totalPacksInCart + quantityDiff > availableCredits) {
      alert(
        teamId
          ? "This quantity would exceed your team's photo pack credit limit. Please contact your workspace owner for more credits."
          : 'This quantity would exceed the maximum number of photo packs allowed.'
      );
      return;
    }

    if (mode === 'cart') {
      // @ts-ignore
      setModel(data.model);
    }

    if (!existingTheme) {
      addTheme({
        id: data.id,
        name: data.name,
        image: data.image,
        photoSize: {
          aspectRatio: data.photoSize?.aspectRatio || DEFAULT_PHOTO_ASPECT_RATIO,
          size: data.photoSize?.size || 'small'
        },
        quantity: data.quantity,
        modifiers: data.modifiers,
        selectedModifiers: data.selectedModifiers,
        workflowName: data.workflowName || getDefaultWorkflowName('flux.1-dev'),
        photoQuantity: data.photoQuantity,
        garmentImages: data.garmentImages,
        slug: data.slug
      });
      onThemeAdded?.();
    } else {
      updateThemePhotoSize(
        data.id,
        data.photoSize!.aspectRatio || DEFAULT_PHOTO_ASPECT_RATIO,
        data.photoSize!.size || 'small'
      );
      updateThemeQuantity(data.id, data.quantity!);
      if (data.selectedModifiers) {
        // updateBulkThemeSelectedModifiers(data.id, data.selectedModifiers);
      }
      updateThemeWorkflow?.(data.id, data.workflowName || getDefaultWorkflowName('flux.1-dev'));
      updateThemeGarmentImages?.(data.id, data.garmentImages || []);
    }

    handleClose();
  };
  const handleClose = () => {
    setIsPhotoPackDetailsOpen(false);
  };

  const currentPhotoPacks = selectedPhotoPacks || themes;

  const handlePhotoPackClick = (theme: Pick<ThemeConfiguration, 'id' | 'name'>) => {
    trackEvent('photoPackClicked', {
      value: theme.id,
      metadata: {
        packName: theme.name,
        section: listTitle
      }
    });
    if (onPhotoPackSelected) {
      // If a callback is provided, call it with the theme, this means the parent component will handle the interaction logic
      onPhotoPackSelected(theme);
    } else {
      // Otherwise, use the default theme selection logic
      handleThemeSelected?.({ id: theme.id });
    }
  };

  if (photoPacksQuery.isPending) {
    return (
      <CardCarousel
        variant={variant}
        hideArrows={true}
        className={cn(
          'w-full',
          listType === 'carousel' && 'w-[calc(100vw-16px)] -mx-6 md:w-full md:mx-0',
          variant === 'samples' && listType === 'carousel' && '-mx-4 w-[calc(100vw-16px)]',
          variant === 'hero' && 'w-[calc(100vw-16px)] -mx-6'
        )}
      >
        {[...Array(Math.min(displayLimit, 8))].map((_, index) => (
          <PhotoPackV3 key={index} variant={variant} name="" photoPackId="" skeleton={true} />
        ))}
      </CardCarousel>
    );
  }

  if (photoPacksQuery.isError) {
    return <div>Error loading photo packs: {photoPacksQuery.error.message}</div>;
  }

  const renderPhotoPacks = (pack: any) => {
    return (
      <div key={pack.id} className={cn(hrefBase ? 'text-current' : 'group relative')}>
        <PhotoPackV3
          variant={variant}
          name={pack.name ?? (hrefBase ? '' : 'Untitled')}
          accentColor={pack.accentColor ?? ''}
          photoPackId={pack.id ?? ''}
          photoCount={40}
          showPhotoCount={true}
          checked={currentPhotoPacks?.some(t => t.id === pack.id)}
          usageCount={pack.usageCount}
          enableSampleGallery={enableSampleGallery}
          sampleImages={pack.sampleImages}
          tag={pack.tags?.[0] ?? ''}
          showUsageCount={showUsageCount}
          relationship={relationship}
          onCheckedChange={() => handlePhotoPackClick(pack)}
          hrefBase={disabledLink ? undefined : hrefBase}
          disableLink={disabledLink}
          showSelfies={showSelfies}
          cloudflareVariant={cloudflareVariant}
          slug={pack.slug}
          primaryDarkColor={pack.primaryDarkColor}
          secondaryDarkColor={pack.secondaryDarkColor}
        />
      </div>
    );
  };

  // Calculate number of items to show based on rowsToShow and columns
  const getRowItems = () => {
    // For lg breakpoint with 5 columns, show 5 * rowsToShow items
    return columns.lg ? columns.lg * rowsToShow : 10;
  };

  // Get the number of items to show in collapsed state
  const collapsedItems = getRowItems();

  return (
    <>
      <div className={cn('w-full', variant === 'listItem' ? '' : '', className)}>
        {(variant !== 'listItem' || listType === 'carousel') && !hideListTitleAndShowAll && (
          <div className={`${alignHeader === 'left' ? 'text-left' : 'text-center mx-auto'} mb-2 space-y-2`}>
            <div className="flex flex-row justify-between items-center w-full">
              <h3 className="text-xl md:text-2xl font-semibold tracking-tight capitalize">{listTitle}</h3>
              {showAllHref && listType === 'carousel' && (
                <Button variant="link" size="sm" className="!text-foreground" asChild>
                  <Link href={showAllHref}>Show all</Link>
                </Button>
              )}
            </div>
            {listDescription && <p className="max-w-2xl text-lg leading-8 text-muted-foreground">{listDescription}</p>}
          </div>
        )}
        <div
          className={cn(
            'w-full',
            listType === 'carousel' && 'w-[calc(100vw-16px)] -mx-6 md:w-full md:mx-0',
            variant === 'samples' && listType === 'carousel' && '-mx-4 w-[calc(100vw-16px)]',
            variant === 'hero' && 'w-[calc(100vw-16px)] -mx-6'
          )}
        >
          {listType === 'carousel' ? (
            <CardCarousel variant={variant} autoplay={autoplay}>
              {displayData?.slice(0, displayLimit ?? Infinity).map(renderPhotoPacks)}
            </CardCarousel>
          ) : (
            <div>
              {variant === 'listItem' ? (
                <Card className="h-full bg-muted/30">
                  <div className="p-5">
                    <h3 className="text-xl font-semibold mb-4">{listTitle}</h3>
                    <div className="space-y-4">
                      {isCollapsible && !isOpen
                        ? displayData?.slice(0, collapsedItems).map((pack, index) => (
                            <div key={pack.id} className={cn(index > 0 && '')}>
                              {renderPhotoPacks(pack)}
                            </div>
                          ))
                        : displayData?.map((pack, index) => (
                            <div key={pack.id} className={cn(index > 0 && '')}>
                              {renderPhotoPacks(pack)}
                            </div>
                          ))}
                    </div>
                  </div>
                </Card>
              ) : (
                <>
                  <CardGrid columns={columns} className="gap-4">
                    {isCollapsible && !isOpen
                      ? displayData?.slice(0, collapsedItems).map(renderPhotoPacks)
                      : displayData?.slice(0, displayLimit ?? Infinity).map(renderPhotoPacks)}
                  </CardGrid>
                </>
              )}
              {isCollapsible && displayData && displayData.length > collapsedItems && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="w-full mt-4 flex items-center justify-center gap-2 text-muted-foreground hover:text-foreground"
                  onClick={() => setIsOpen(!isOpen)}
                >
                  {isOpen ? (
                    <>
                      Show Less <ChevronUp className="h-4 w-4" />
                    </>
                  ) : (
                    <>
                      Show More <ChevronDown className="h-4 w-4" />
                    </>
                  )}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
      {isPhotoPackDetailsOpen && selectedThemeId && (
        <PhotoPackDetails
          modelLocked={modelLocked}
          open={isPhotoPackDetailsOpen}
          initialValues={selectedTheme!}
          onClose={handleClose}
          modelVersion={'flux.1-dev'}
          onSubmit={handleSubmit}
          themeId={selectedThemeId}
          modelSelectorMode={mode === 'training' ? 'training' : 'selection'}
          onRemove={() => {
            removeTheme(selectedThemeId);
            handleClose();
          }}
        />
      )}
    </>
  );
};
